import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { UserAvatar } from "@/components/identity/UserAvatar";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useAccessControl } from "@/hooks/useAccessControl";
import { useSpotStudioAccess } from "@/hooks/useSpotStudio";

import {
  CONTROL_PANEL_HEADER_ACTIONS,
  CONTROL_PANEL_SECTIONS,
  type ControlPanelSectionConfig,
} from "./controlPanelConfig";

/**
 * Trigger button shown in the top bar (desktop public routes / mobile)
 * — lets the user open the panel as a drawer at any time.
 */
export function ControlPanelTrigger({ className }: { className?: string }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const { profile } = useProfile();
  if (!user) return null;
  const avatarPath = profile?.avatar_url;
  const displayLabel = profile?.display_name || profile?.first_name || "";
  return (
    <PanelSheet
      trigger={
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "gap-2.5 rounded-xl hover:bg-primary/[0.06] px-2 h-9 transition-all duration-200",
            className,
          )}
          aria-label={t("controlPanel.openLabel")}
        >
          <UserAvatar
            path={avatarPath}
            displayName={displayLabel}
            size={28}
            shape="square"
            className="rounded-lg ring-1 ring-border/30"
          />
          {displayLabel && (
            <span className="hidden sm:inline max-w-[100px] truncate text-xs font-medium text-foreground">
              {displayLabel}
            </span>
          )}
        </Button>
      }
    />
  );
}

/**
 * Imperative trigger used by mobile bottom-nav (no avatar — uses an
 * existing visual that the parent supplies).
 */
export function ControlPanelExternalTrigger({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { user } = useAuth();
  if (!user) return null;
  return <PanelSheet open={open} onOpenChange={onOpenChange} />;
}

// ──────────────────────────────────────────────────────────────────────
// Internals
// ──────────────────────────────────────────────────────────────────────

function PanelSheet({
  trigger,
  open,
  onOpenChange,
}: {
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (next: boolean) => void;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const value = isControlled ? open : internalOpen;

  // Keep the latest setter in a ref so closePanel stays referentially
  // stable even when the parent recreates `onOpenChange` on every render.
  const setterRef = useRef<(next: boolean) => void>(setInternalOpen);
  setterRef.current = isControlled ? (onAtCall) => onOpenChange!(onAtCall) : setInternalOpen;

  const handleOpenChange = useCallback((next: boolean) => {
    setterRef.current(next);
  }, []);

  const closePanel = useCallback(() => {
    setterRef.current(false);
  }, []);

  return (
    <Sheet open={value} onOpenChange={handleOpenChange}>
      {trigger ? <SheetTrigger asChild>{trigger}</SheetTrigger> : null}
      <SheetContent
        side="right"
        className="w-full sm:max-w-[min(420px,92vw)] md:max-w-md p-0 flex flex-col gap-0 z-[200] pt-safe pb-safe"
      >
        {/* PanelBody is only mounted while the sheet is open (Radix
            unmounts content on close by default), so the pathname
            effect inside it can safely act as the close-on-navigate
            safety net. */}
        <PanelBody onClose={closePanel} />
      </SheetContent>
    </Sheet>
  );
}

function PanelBody({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const { canPerformAction } = useAccessControl();
  const { canAccess: canSpotStudio } = useSpotStudioAccess();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  // Single source of truth for "navigation happened, close the
  // overlay". All in-panel handlers call this exact function — no
  // inline arrows, no duplicated semantics.
  const handleNavigate = onClose;

  // Safety net: close the panel whenever the route changes from any
  // source (link click inside the panel, browser back, programmatic
  // navigate, etc.). PanelBody only exists while the sheet is open,
  // so the very first render's [pathname, search] capture is the
  // baseline; subsequent route changes trigger close. setOpen(false)
  // is idempotent, so this is safe even when an inner handler has
  // already closed the sheet.
  const baselineRef = useRef<string>(`${pathname}${search}`);
  useEffect(() => {
    const next = `${pathname}${search}`;
    if (next !== baselineRef.current) {
      baselineRef.current = next;
      onClose();
    }
  }, [pathname, search, onClose]);

  const adminAllowed = canPerformAction("admin.access").allowed;

  const handleLogout = useCallback(async () => {
    // Close immediately so the panel doesn't linger during the async
    // signOut roundtrip.
    onClose();
    await signOut();
    queryClient.removeQueries({ queryKey: ["user-roles"] });
    queryClient.removeQueries({ queryKey: ["all-user-roles"] });
    queryClient.removeQueries({ queryKey: ["admin-stats"] });
    toast.success(t("auth.logout"));
    navigate("/");
  }, [onClose, signOut, queryClient, t, navigate]);

  const sectionVisible = (s: ControlPanelSectionConfig) => {
    if (s.permission === "spotStudio") return canSpotStudio;
    if (s.permission === "admin") return adminAllowed;
    return true;
  };

  const avatarPath = profile?.avatar_url;
  const displayLabel = profile?.display_name || profile?.first_name || "";

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header — pr-12 reserves space for the SheetContent close button
          which is absolutely positioned at top-4 right-4 inside the sheet. */}
      <div className="px-4 pr-12 pt-5 pb-4 border-b border-border/40">
        <Link
          to="/profile"
          onClick={handleNavigate}
          className="flex items-center gap-3 rounded-xl hover:bg-primary/[0.05] -mx-2 px-2 py-1.5 transition-colors"
        >
          <UserAvatar
            path={avatarPath}
            displayName={displayLabel}
            size={44}
            shape="rounded"
            className="ring-1 ring-border/40 shrink-0"
          />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-foreground truncate">
              {displayLabel || t("nav.profile")}
            </div>
            {user?.email && (
              <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                {user.email}
              </div>
            )}
          </div>
        </Link>

        {/* Quick actions — pill buttons mit sichtbarem Label, damit
            der Header sofort verständlich bleibt (v3.3). */}
        <div className="mt-3 grid grid-cols-3 gap-2">
          <HeaderQuickAction
            to={CONTROL_PANEL_HEADER_ACTIONS.profile.to}
            icon={<CONTROL_PANEL_HEADER_ACTIONS.profile.icon className="h-[18px] w-[18px]" strokeWidth={1.75} />}
            label={t(CONTROL_PANEL_HEADER_ACTIONS.profile.labelKey)}
            onSelect={handleNavigate}
          />
          <HeaderQuickActionDisabled
            icon={<CONTROL_PANEL_HEADER_ACTIONS.communication.icon className="h-[18px] w-[18px]" strokeWidth={1.75} />}
            label={t(CONTROL_PANEL_HEADER_ACTIONS.communication.labelKey)}
            tooltip={t(CONTROL_PANEL_HEADER_ACTIONS.communication.soonKey)}
            unreadCount={0}
          />
          <HeaderQuickAction
            to={CONTROL_PANEL_HEADER_ACTIONS.settings.to}
            icon={<CONTROL_PANEL_HEADER_ACTIONS.settings.icon className="h-[18px] w-[18px]" strokeWidth={1.75} />}
            label={t(CONTROL_PANEL_HEADER_ACTIONS.settings.labelKey)}
            onSelect={handleNavigate}
          />
        </div>
      </div>

      {/* Sections */}
      <nav className="flex-1 min-h-0 overflow-y-auto py-3">
        {CONTROL_PANEL_SECTIONS.filter(sectionVisible).map((section, idx) => (
          <PanelSection
            key={section.key}
            section={section}
            isFirst={idx === 0}
            onNavigate={handleNavigate}
            soonLabel={t("controlPanel.soon")}
            betaLabel={t("controlPanel.beta")}
            sectionTitle={t(section.titleKey)}
          />
        ))}
      </nav>

      {/* Logout — bewusst zurückhaltend, damit es die Navigation nicht
          überstrahlt. Klar erreichbar, aber kein primäres CTA. */}
      <div className="border-t border-border/40 px-3 py-2.5">
        <button
          type="button"
          onClick={handleLogout}
          className={cn(
            "w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg",
            "text-xs font-medium text-muted-foreground",
            "hover:text-destructive hover:bg-destructive/[0.06]",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive/40",
            "transition-colors duration-200",
          )}
        >
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.75} aria-hidden />
          <span>{t("auth.logout")}</span>
        </button>
      </div>
    </div>
  );
}

function PanelSection({
  section,
  isFirst,
  onNavigate,
  soonLabel,
  betaLabel,
  sectionTitle,
}: {
  section: ControlPanelSectionConfig;
  isFirst: boolean;
  onNavigate: () => void;
  soonLabel: string;
  betaLabel: string;
  sectionTitle: string;
}) {
  // "Demnächst" startet eingeklappt — disabled Items dürfen nicht
  // visuell dominieren.
  const isComingSoon = section.key === "comingSoon";
  const [collapsed, setCollapsed] = useState(isComingSoon);

  return (
    <div className={cn("px-3", !isFirst && "mt-2 pt-3 border-t border-border/40")}>
      {isComingSoon ? (
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          className="w-full flex items-center justify-between px-3.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/70 hover:text-foreground transition-colors"
        >
          <span>{sectionTitle}</span>
          <ChevronDown
            className={cn("h-3 w-3 transition-transform", collapsed && "-rotate-90")}
            strokeWidth={2}
            aria-hidden
          />
        </button>
      ) : (
        <div className="px-3.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/70">
          {sectionTitle}
        </div>
      )}
      {!collapsed && (
        <ul className="space-y-0.5">
          {section.items.map((item) => (
            <li key={item.key}>
              <PanelRow
                item={item}
                onNavigate={onNavigate}
                soonLabel={soonLabel}
                betaLabel={betaLabel}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PanelRow({
  item,
  onNavigate,
  soonLabel,
  betaLabel,
}: {
  item: import("./controlPanelConfig").ControlPanelItem;
  onNavigate: () => void;
  soonLabel: string;
  betaLabel: string;
}) {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const Icon = item.icon;
  const label = t(item.labelKey);
  const disabled = !item.to;

  if (disabled) {
    return (
      <div
        aria-disabled="true"
        className="flex items-center gap-2.5 px-3.5 py-1.5 rounded-lg text-[12px] text-muted-foreground/60 cursor-not-allowed"
      >
        <Icon className="w-3.5 h-3.5 opacity-70" strokeWidth={1.75} aria-hidden />
        <span className="flex-1 truncate">{label}</span>
        <span className="text-[9px] font-medium uppercase tracking-wide rounded-full bg-muted/60 px-1.5 py-px text-muted-foreground/80">
          {soonLabel}
        </span>
      </div>
    );
  }

  const active =
    pathname === item.to || pathname.startsWith(`${item.to.split("?")[0]}/`);

  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      className={cn(
        "flex items-center gap-3 px-3.5 py-2 rounded-xl text-sm font-medium transition-colors duration-200",
        active
          ? "bg-primary/[0.10] text-foreground"
          : "text-foreground/85 hover:bg-primary/[0.06] hover:text-foreground",
      )}
    >
      <Icon
        className={cn("w-4 h-4", active ? "text-primary" : "text-muted-foreground")}
        strokeWidth={1.75}
        aria-hidden
      />
      <span className="flex-1 truncate">{label}</span>
      {item.beta && (
        <span className="text-[9.5px] font-semibold uppercase tracking-wide rounded-full bg-primary/10 text-primary px-1.5 py-0.5">
          {betaLabel}
        </span>
      )}
    </Link>
  );
}

function HeaderQuickAction({
  to,
  icon,
  label,
  onSelect,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  onSelect?: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onSelect}
      aria-label={label}
      className="flex flex-col items-center justify-center gap-1 h-[58px] min-h-[44px] rounded-xl border border-border/30 bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-primary/[0.08] hover:border-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-colors"
    >
      {icon}
      <span className="text-[10px] font-medium leading-none">{label}</span>
    </Link>
  );
}

function HeaderQuickActionDisabled({
  icon,
  label,
  tooltip,
  unreadCount = 0,
}: {
  icon: ReactNode;
  label: string;
  tooltip: string;
  unreadCount?: number;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-label={label}
          aria-disabled="true"
          className="relative flex flex-col items-center justify-center gap-1 h-[58px] min-h-[44px] rounded-xl border border-dashed border-border/40 bg-muted/20 text-muted-foreground/55 cursor-not-allowed"
        >
          {icon}
          <span className="text-[10px] font-medium leading-none">{label}</span>
          {unreadCount > 0 && (
            <span
              aria-label={`${unreadCount} ungelesen`}
              className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-primary text-[10px] leading-[16px] font-semibold text-primary-foreground text-center"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}